import { useTranslation } from 'next-i18next';

import { Flex, Heading, Text } from '@chakra-ui/react';

import { scrollToSection } from '@nocowanie/common-ui';
import { IconsLinear } from '@nocowanie/icons';

import {
    AccommodationBadges,
    AccommodationBadgeType,
} from '@app/components/molecules/accommodation-badges';
import { Rating } from '@app/components/molecules/rating';
import { Label } from '@app/models/api/api-types';
import { accommodationStaticSlice, commonUiStateSlice, useAppSelector } from '@app/state';

import type { BasicInformationProps } from './basic-information.props';

export const BasicInformation = ({
    scollToLocationRef,
    scollToReviewsRef,
    ...props
}: BasicInformationProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'basicInformation',
    });

    const {
        name,
        address,
        streetNumber,
        postalCode,
        settlementName,
        ratingAverage,
        ratingCount,
        ratingDescription,
        internalLabel,
        labels,
    } = useAppSelector(accommodationStaticSlice.selectors.selectBasicInformationData);

    const isMobile = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);

    const badgesToShow: AccommodationBadgeType[] = [
        ...(internalLabel?.reservationType === 'Query'
            ? [AccommodationBadgeType.ConfirmationUpTo24Hours]
            : []),
        ...(internalLabel?.reservationType === 'RealTime'
            ? [AccommodationBadgeType.InstantReservation]
            : []),
        ...(labels?.includes(Label.PromotedInTv) ? [AccommodationBadgeType.PromotedOnTV] : []),
        ...(ratingAverage && ratingCount ? [] : [AccommodationBadgeType.NewAccommodation]),
    ];

    return (
        <>
            <Flex
                {...props}
                flexDirection="row"
                justifyContent={'space-between'}
                alignItems={'flex-start'}
            >
                <Flex direction={'column'} gap={2} w={'full'}>
                    <Flex alignItems={'center'} flexWrap={'wrap'}>
                        <Heading
                            as={'h1'}
                            lineClamp={{
                                base: 2,
                                md: 1,
                            }}
                            size={{
                                base: 'md',
                                md: 'xl',
                            }}
                            overflow={'hidden'}
                            mr={!isMobile ? 3 : 0}
                            data-testid="basic-info-title"
                        >
                            {name}
                        </Heading>
                        {!isMobile ? (
                            <AccommodationBadges
                                display={'flex'}
                                flex={1}
                                badges={badgesToShow}
                                data-testid="basic-info-badges"
                            />
                        ) : null}
                    </Flex>
                    <Flex
                        as="button"
                        onClick={() => {
                            scrollToSection(scollToLocationRef);
                        }}
                        fontSize={'sm'}
                        alignItems={'center'}
                        gap={1.5}
                    >
                        <IconsLinear.Location size={'xs'} color={'gray.700'} mt={-0.5} />
                        <Text
                            color={{
                                base: 'secondary.solid',
                                md: 'gray.700',
                            }}
                            textAlign={'left'}
                            data-testid="basic-info-address"
                            as={'span'}
                        >
                            {address} {streetNumber}, {postalCode} {settlementName}
                        </Text>
                        <Text
                            as={'span'}
                            color="secondary.solid"
                            cursor={'pointer'}
                            hideBelow={'lg'}
                        >
                            {t('checkOnMap')}
                        </Text>
                    </Flex>
                </Flex>
                {ratingAverage && ratingCount && ratingDescription ? (
                    <Rating
                        ml={'gutterWidth'}
                        ratingAverage={ratingAverage}
                        ratingCount={ratingCount}
                        ratingDescription={ratingDescription}
                        hideLabelsOnMobile
                        onClick={() => {
                            scrollToSection(scollToReviewsRef);
                        }}
                        flex={'0 0 auto'}
                        cursor={'pointer'}
                        ratingProps={{
                            size: 'lg',
                        }}
                    />
                ) : null}
            </Flex>
            {isMobile ? (
                <AccommodationBadges badges={badgesToShow} mt={2} data-testid="basic-info-badges" />
            ) : null}
        </>
    );
};
